import { Client } from 'xrpl';

export class XrplClient {
  static MAINNET_URL = 'wss://xrplcluster.com';
  
  static async getClient() {
    // Try multiple server connections in case one fails
    const servers = [
      'wss://xrplcluster.com',
      'wss://s1.ripple.com',
      'wss://s2.ripple.com'
    ];
    
    let lastError = null;
    
    // Try each server in order
    for (const server of servers) {
      try {
        const client = new Client(server, {
          // Increase timeout to prevent timeout errors
          timeout: 20000,  // 20 seconds instead of default
          connectionTimeout: 15000  // 15 seconds for connection
        });
        await client.connect();
        console.log(`Connected to XRPL server: ${server}`);
        return client;
      } catch (err) {
        console.warn(`Failed to connect to ${server}:`, err.message);
        lastError = err;
      }
    }
    
    // If all servers failed, throw the last error
    throw new Error(`Failed to connect to any XRPL server: ${lastError?.message}`);
  }

  // Add a method to check if an account exists
  static async accountExists(address) {
    if (!address) {
      console.error("No address provided to accountExists method");
      return false;
    }
    
    // Maximum number of retries for timeout errors
    const MAX_RETRIES = 3;
    
    for (let retry = 0; retry < MAX_RETRIES; retry++) {
      let client = null;
      
      try {
        client = await this.getClient();
        
        console.log(`Attempt ${retry + 1}/${MAX_RETRIES} to check if account ${address} exists...`);
        
        const response = await client.request({
          command: "account_info",
          account: address,
          ledger_index: "validated"
        });
        
        // If we get a successful response, the account exists and we can extract the balance
        if (response && response.result && response.result.account_data) {
          const balanceInDrops = response.result.account_data.Balance;
          const xrpBalance = (parseInt(balanceInDrops) / 1000000).toFixed(6);
          console.log(`Account ${address} exists with balance: ${xrpBalance} XRP`);
          
          // Make sure to disconnect before returning
          if (client) {
            try {
              await client.disconnect();
            } catch (disconnectError) {
              console.warn("Error disconnecting client:", disconnectError.message);
            }
          }
          
          return true;
        }
        
        return false;
      } catch (error) {
        // Handle specific error for account not found - we can exit immediately
        if (error.data && error.data.error === 'actNotFound') {
          console.log(`Account ${address} does not exist on the ledger`);
          
          // Make sure to disconnect before returning
          if (client) {
            try {
              await client.disconnect();
            } catch (disconnectError) {
              console.warn("Error disconnecting client:", disconnectError.message);
            }
          }
          
          return false;
        }
        
        // For timeout errors, we'll retry
        if (error.name === 'TimeoutError' || 
            error.message?.includes('Timeout') || 
            error.message?.includes('timeout')) {
          console.warn(`Timeout checking account ${address} (attempt ${retry + 1}/${MAX_RETRIES}):`, error.message);
          
          // Make sure to disconnect before retrying
          if (client) {
            try {
              await client.disconnect();
            } catch (disconnectError) {
              console.warn("Error disconnecting client:", disconnectError.message);
            }
          }
          
          // If we've tried the maximum number of times, assume the account might exist
          if (retry === MAX_RETRIES - 1) {
            console.log(`After ${MAX_RETRIES} timeout retries, assuming account ${address} exists`);
            return true;
          }
          
          // Wait before retrying (exponential backoff)
          const delay = Math.pow(2, retry) * 1000; // 1s, 2s, 4s
          await new Promise(resolve => setTimeout(resolve, delay));
          continue; // Try again
        }
        
        // For other errors, log them but assume the account might exist
        console.error("Error checking account existence:", error);
        
        // Make sure to disconnect before returning
        if (client) {
          try {
            await client.disconnect();
          } catch (disconnectError) {
            console.warn("Error disconnecting client:", disconnectError.message);
          }
        }
        
        console.log("Due to unexpected error, assuming account exists");
        return true;
      }
    }
    
    // If we've retried maximum times, assume account exists
    console.log(`After ${MAX_RETRIES} retries, assuming account ${address} exists`);
    return true;
  }

  static async blackHoleWallet(wallet) {
    // First check if the wallet account exists
    const exists = await this.accountExists(wallet.address);
    if (!exists) {
      throw new Error("Account not found. The wallet needs to be funded with at least 1 XRP to exist on the ledger.");
    }
    
    const client = await this.getClient();
    try {
      // Implement mainnet black hole logic
      const tx = {
        TransactionType: "AccountSet",
        Account: wallet.address,
        SetFlag: 4, // Enable master key disabling
        SigningPubKey: "00000000000000000000000000000000000000000000000000000000000000000"
      };
      
      const prepared = await client.autofill(tx);
      const signed = wallet.sign(prepared);
      const result = await client.submitAndWait(signed.tx_blob);
      
      return result;
    } finally {
      await client.disconnect();
    }
  }

  static async burnLP(wallet) {
    // First check if the wallet account exists
    const exists = await this.accountExists(wallet.address);
    if (!exists) {
      throw new Error("Account not found. The wallet needs to be funded with at least 1 XRP to exist on the ledger.");
    }
    
    const client = await this.getClient();
    try {
      // Implement mainnet LP burning logic
      // This would involve sending all assets to a black hole address
      const blackHoleAddress = "rrrrrrrrrrrrrrrrrrrrrhoLvTp";
      
      // Get account info to check balance
      const accountInfo = await client.request({
        command: "account_info",
        account: wallet.address
      });
      
      const tx = {
        TransactionType: "Payment",
        Account: wallet.address,
        Destination: blackHoleAddress,
        Amount: accountInfo.result.account_data.Balance
      };
      
      const prepared = await client.autofill(tx);
      const signed = wallet.sign(prepared);
      const result = await client.submitAndWait(signed.tx_blob);
      
      return result;
    } finally {
      await client.disconnect();
    }
  }

  // Fund another wallet from an active wallet
  static async fundWallet(sourceWallet, destinationAddress, amountXRP = 1.2) {
    if (!sourceWallet || !destinationAddress) {
      throw new Error("Source wallet and destination address are required for funding");
    }

    // Validate source wallet exists and has sufficient funds
    const sourceExists = await this.accountExists(sourceWallet.address);
    if (!sourceExists) {
      throw new Error("Source wallet not found on the ledger or is not activated");
    }

    const client = await this.getClient();
    try {
      // Get source account info to check balance
      const accountInfo = await client.request({
        command: "account_info",
        account: sourceWallet.address,
        ledger_index: "validated"
      });

      const currentBalance = parseInt(accountInfo.result.account_data.Balance) / 1000000;
      
      // Ensure source wallet has enough XRP (minimum balance + amount to send + transaction fee)
      const minimumSourceBalance = 10; // XRP required for source wallet to keep
      if (currentBalance < (minimumSourceBalance + amountXRP + 0.00001)) {
        throw new Error(`Insufficient balance in source wallet. Minimum required: ${minimumSourceBalance + amountXRP + 0.00001} XRP, Current balance: ${currentBalance} XRP`);
      }

      console.log(`Funding wallet ${destinationAddress} with ${amountXRP} XRP from ${sourceWallet.address}...`);
      
      // Convert XRP to drops (1 XRP = 1,000,000 drops)
      const amountDrops = Math.floor(amountXRP * 1000000).toString();
      
      // Prepare the payment transaction
      const tx = {
        TransactionType: "Payment",
        Account: sourceWallet.address,
        Destination: destinationAddress,
        Amount: amountDrops,
        Fee: "12"
      };
      
      const prepared = await client.autofill(tx);
      const signed = sourceWallet.sign(prepared);
      const result = await client.submitAndWait(signed.tx_blob);
      
      if (result.result.meta.TransactionResult !== "tesSUCCESS") {
        throw new Error(`Funding transaction failed: ${result.result.meta.TransactionResult}`);
      }
      
      console.log(`Successfully funded wallet ${destinationAddress} with ${amountXRP} XRP. Transaction hash: ${result.result.hash}`);
      
      return result;
    } finally {
      if (client.isConnected()) {
        await client.disconnect();
      }
    }
  }
} 